import React from 'react'
import { motion } from 'framer-motion'
import {
  StyleImage,
  StyleTextButtonWrapper,
  StyleUniversalBannerWrapper,
} from './style'
import { TextWrapper, TextWrapperAlternate } from '@pages/front/commons/Text'
import Button from '@components/button'
import { StyleContentWrapper } from '@pages/front/commons/ContentWrapper/style'
import person from './person.png'
import { StyleImageWrapper } from '@pages/front/commons/ImageWrapper/style'
import { ConsultNowIcon } from '@pages/front/svgs/svg'

// Create motion-enhanced versions of styled components
const MotionUniversalBannerWrapper = motion(StyleUniversalBannerWrapper)
const MotionTextButtonWrapper = motion(StyleTextButtonWrapper)
const MotionImage = motion(StyleImage)

interface IUniversalBanner {
  bgColor?: string
  imgSrc?: string
  title?: string
  subtitle?: string
  hasButton?: boolean
  titleColor?: string
  subtitleColor?: string
}

// Animation variants
const bannerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      when: 'beforeChildren',
    },
  },
}

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
}

const imageVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: 'easeOut' },
  },
}

const buttonVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: 0.3 },
  },
  hover: {
    scale: 1.02,
    transition: { duration: 0.2 },
  },
  tap: {
    scale: 0.98,
  },
}

const UniveralBanner = ({
  bgColor,
  imgSrc,
  title,
  subtitle,
  hasButton = true,
  titleColor,
  subtitleColor,
}: IUniversalBanner) => {
  return (
    <StyleContentWrapper stretch paddingLeft="20" paddingRight="20">
      <MotionUniversalBannerWrapper
        bgColor={bgColor}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '0px 0px -100px 0px' }}
        variants={bannerVariants}
      >
        <MotionTextButtonWrapper variants={contentVariants}>
          <TextWrapperAlternate
            titleColor={titleColor}
            subtitleColor={subtitleColor}
            title={title || 'Keep yourself & Your Family Safe!'}
            subtitle={
              subtitle ||
              `"Your health, your schedule — consult a doctor online with ease."`
            }
            rowGap="8"
          />

          {hasButton && (
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button title="Consult Now" icon={ConsultNowIcon} />
            </motion.div>
          )}
        </MotionTextButtonWrapper>

        <MotionImage src={imgSrc || person} variants={imageVariants} />
      </MotionUniversalBannerWrapper>
    </StyleContentWrapper>
  )
}

export default UniveralBanner
